<template>
  <div>
    <div v-if="$auth.policies['view-meal-config']" class="pa-4">
      <div class="d-flex justify-space-between align-center">
        <Breadcrumbs
          :headline="slot.title || 'Chi tiết bữa ăn'"
          :link="[{ text: 'Dịch vụ bếp', href: '#' }, { text: backText, href: backHref }, { text: slot.title || '' }]"
        />
        <div>
          <v-btn outlined color="primary" class="mr-2" @click="openEditMeal"
            ><v-icon left>edit</v-icon>Sửa thông tin</v-btn
          >
          <v-btn outlined color="red" @click="confirmDeleteMeal"><v-icon left>delete</v-icon>Xóa bữa</v-btn>
        </div>
      </div>
      <MealSlotFormDialog ref="formDialog" :type="slot.type || 'fixed'" @saved="onMealSaved" />

      <!-- Thông tin bữa -->
      <v-card outlined class="pa-4 my-4 elevation-0">
        <v-row dense>
          <v-col cols="6" md="3"><b>Thời điểm:</b> {{ timeOfDayLabel(slot.timeOfDay) }}</v-col>
          <v-col cols="6" md="3"><b>Loại:</b> {{ mealTypeLabel[slot.type] }}</v-col>
          <v-col cols="6" md="3">
            <b>Lịch: </b>
            <span v-if="slot.type === 'fixed'">{{ serveDaysLabel(slot.serveDays) }}</span>
            <span v-else>{{ slot.serveDate | ddmmyyyy }}</span>
          </v-col>
          <v-col cols="6" md="3"><b>Số menu:</b> {{ slot.menuCount }}</v-col>
          <v-col cols="6" md="3"><b>Suất tối đa:</b> {{ slot.maxPortion }}</v-col>
          <v-col cols="6" md="3">
            <b>Biểu phí:</b> {{ slot.feeType === 'free' ? feeTypeLabel('free') : formatMoney(slot.feeAmount) }}
          </v-col>
        </v-row>
      </v-card>

      <!-- Áp dụng theo nhóm -->
      <v-card outlined class="my-4 elevation-0">
        <v-card-title>
          Áp dụng theo nhóm đối tượng
          <v-spacer />
          <v-btn small color="primary" depressed @click="openApplication()"
            ><v-icon left small>add</v-icon>Thêm áp dụng</v-btn
          >
        </v-card-title>
        <v-data-table
          :headers="appHeaders"
          :items="configs"
          :loading="loadingConfigs"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:[`item.group`]="{ item }">{{ item.meal_group && item.meal_group.title }}</template>
          <template v-slot:[`item.isDefaultRegister`]="{ item }">
            <v-chip x-small :color="defaultRegisterColor(item.isDefaultRegister)" dark>
              {{ defaultRegisterLabel(item.isDefaultRegister) }}
            </v-chip>
          </template>
          <template v-slot:[`item.autoBalanceMenu`]="{ item }">{{
            item.autoBalanceMenu ? YES_LABEL : EMPTY_LABEL
          }}</template>
          <template v-slot:[`item.serveDays`]="{ item }">{{
            item.serveDays ? serveDaysLabel(item.serveDays) : SERVE_BY_MEAL_LABEL
          }}</template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn icon small color="orange" @click="openApplication(item)"><v-icon small>edit</v-icon></v-btn>
            <v-btn icon small color="red" @click="confirmDeleteApp(item)"><v-icon small>delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Lịch sử chỉnh sửa: log bữa + áp dụng + khóa của bữa này -->
      <MealConfigHistory v-if="slot.id" ref="history" :slot-id="slot.id" />

      <!-- Dialog Áp dụng -->
      <v-dialog v-model="appDialog" width="560" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>{{ appForm.id ? 'SỬA ÁP DỤNG' : 'THÊM ÁP DỤNG' }}</span>
            <v-spacer /><v-icon color="white" @click="appDialog = false">close</v-icon>
          </v-card-title>
          <v-form ref="appForm" class="pa-5" @keydown.native.enter.prevent="saveApplication">
            <v-autocomplete
              v-model="appForm.meal_group"
              :items="groupOptions"
              item-text="title"
              item-value="id"
              label="Nhóm đối tượng"
              outlined
              dense
              class="mt-5 mb-0"
              :rules="[$rules.required]"
              :disabled="!!appForm.id"
            />
            <v-radio-group v-model="appForm.isDefaultRegister" col class="mb-0" :label="AUTO_REGISTER_LABEL">
              <v-radio :label="YES_LABEL" :value="true" />
              <v-radio :label="NO_LABEL" :value="false" />
            </v-radio-group>
            <v-checkbox
              v-if="appForm.isDefaultRegister && slot.menuCount > 1"
              v-model="appForm.autoBalanceMenu"
              label="Tự động cân bằng menu cho HS không tự đăng ký"
              hide-details
              class="mt-0 mb-8 label-left"
            />

            <v-divider class="mb-4" />
            <div class="subtitle-2 mb-2">Thời gian cho phép phụ huynh:</div>
            <!-- Bữa cố định: serveDays riêng + cửa sổ đăng ký theo offset -->
            <template v-if="slot.type === 'fixed'">
              <v-select
                v-model="appForm.serveDays"
                :items="serveDaysOverrideOptions"
                label="Ngày phục vụ riêng nhóm này"
                hint="Để trống = theo cấu hình của bữa"
                persistent-hint
                clearable
                outlined
                dense
                class="mb-3"
              />
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="appForm.registerOpenBefore"
                    :items="registerOpenBeforeOptions"
                    label="Mở đăng ký"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="appForm.registerCloseBefore"
                    :items="registerCloseBeforeOptions"
                    label="Đóng đăng ký"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="appForm.cancelBefore"
                    :items="cancelBeforeOptions"
                    label="Cho phép hủy"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </template>
            <!-- Bữa bổ sung: ngày mở/đóng/hủy cụ thể -->
            <v-row v-else>
              <v-col cols="12" md="4">
                <DateIOSPicker label="Ngày mở ĐK *" outlined dense :date.sync="appForm.registerOpenDate" />
              </v-col>
              <v-col cols="12" md="4">
                <DateIOSPicker label="Ngày đóng ĐK *" outlined dense :date.sync="appForm.registerCloseDate" />
              </v-col>
              <v-col cols="12" md="4">
                <DateIOSPicker label="Ngày được hủy *" outlined dense :date.sync="appForm.cancelDate" required />
              </v-col>
            </v-row>
          </v-form>
          <v-divider class="mb-2" />
          <v-alert dense text type="info" class="caption mx-4 mb-0">{{ effectNote }}</v-alert>
          <v-card-actions>
            <v-btn outlined @click="appDialog = false">Hủy</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed :loading="savingApp" @click="saveApplication">Lưu</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog Khóa -->
      <v-dialog v-model="lockDialog" width="520" :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-card-title class="primary white--text">
            <span>{{ lockForm.id ? 'SỬA KHÓA' : 'THÊM KHÓA' }}</span
            ><v-spacer /><v-icon color="white" @click="lockDialog = false">close</v-icon>
          </v-card-title>
          <v-form ref="lockForm" class="pa-4" @keydown.native.enter.prevent="saveLock">
            <v-autocomplete
              v-model="lockForm.meal_group"
              :items="groupOptions"
              item-text="title"
              item-value="id"
              label="Nhóm đối tượng bị khóa"
              hint="Chọn bất kỳ nhóm: toàn trường / cấp / khối / lớp / nhóm tự tạo"
              persistent-hint
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-row>
              <v-col cols="12" md="6">
                <DateIOSPicker label="Từ ngày *" outlined dense :date.sync="lockForm.fromDate" />
              </v-col>
              <v-col cols="12" md="6">
                <DateIOSPicker label="Đến ngày (tùy chọn)" outlined dense :date.sync="lockForm.toDate" />
              </v-col>
            </v-row>
          </v-form>
          <v-alert dense text type="info" class="caption mx-4 mb-0">{{ effectNote }}</v-alert>
          <v-card-actions>
            <v-btn outlined @click="lockDialog = false">Hủy</v-btn>
            <v-spacer />
            <v-btn color="primary" depressed :loading="savingLock" @click="saveLock">Lưu</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <Page403 v-else />
  </div>
</template>

<script>
import Breadcrumbs from '@/components/layout/Breadcrumbs'
import Page403 from '@/views/Page403.vue'
import DateIOSPicker from '@/components/basic/picker/DateIOSPicker.vue'
import MealSlotFormDialog from '@/modules/mealConfig/meal-slot/MealSlotFormDialog'
import MealConfigHistory from '@/modules/mealConfig/MealConfigHistory'
import { MealGroup } from '@/plugins/api'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import {
  MEAL_TYPE_LABEL,
  SERVE_DAYS_OPTIONS,
  REGISTER_OPEN_BEFORE_OPTIONS,
  REGISTER_CLOSE_BEFORE_OPTIONS,
  CANCEL_BEFORE_OPTIONS,
  timeOfDayLabel,
  serveDaysLabel,
  feeTypeLabel,
  defaultRegisterLabel,
  defaultRegisterColor,
  mealTypeFullLabel,
  NO_END_DATE_LABEL,
  SERVE_BY_MEAL_LABEL,
  YES_LABEL,
  NO_LABEL,
  AUTO_REGISTER_LABEL,
  EMPTY_LABEL,
  EFFECT_NOTE
} from '@/modules/mealConfig/mealConfigConstants'

export default {
  components: { Breadcrumbs, Page403, DateIOSPicker, MealSlotFormDialog, MealConfigHistory },
  filters: {
    ddmmyyyy(v) {
      return v ? moment(v).format('DD/MM/YYYY') : ''
    }
  },
  data() {
    return {
      slot: {},
      groupOptions: [],
      mealTypeLabel: MEAL_TYPE_LABEL,
      YES_LABEL,
      NO_LABEL,
      AUTO_REGISTER_LABEL,
      EMPTY_LABEL,
      NO_END_DATE_LABEL,
      SERVE_BY_MEAL_LABEL,
      effectNote: EFFECT_NOTE,
      serveDaysOverrideOptions: SERVE_DAYS_OPTIONS,
      registerOpenBeforeOptions: REGISTER_OPEN_BEFORE_OPTIONS,
      registerCloseBeforeOptions: REGISTER_CLOSE_BEFORE_OPTIONS,
      cancelBeforeOptions: CANCEL_BEFORE_OPTIONS,
      appDialog: false,
      savingApp: false,
      appForm: this.emptyApp(),
      lockDialog: false,
      savingLock: false,
      lockForm: { id: null, meal_group: null, fromDate: null, toDate: null },
      appHeaders: [
        { text: 'Nhóm', value: 'group', sortable: false },
        { text: 'Mặc định', value: 'isDefaultRegister', sortable: false },
        { text: 'Tự cân bằng', value: 'autoBalanceMenu', sortable: false },
        { text: 'Phục vụ riêng', value: 'serveDays', sortable: false },
        { text: 'Thao tác', value: 'action', sortable: false, align: 'center' }
      ],
      lockHeaders: [
        { text: 'Nhóm', value: 'group', sortable: false },
        { text: 'Từ ngày', value: 'fromDate', sortable: false },
        { text: 'Đến ngày', value: 'toDate', sortable: false },
        { text: 'Thao tác', value: 'action', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    ...mapState('mealGroupConfig', { configs: 'configs', loadingConfigs: 'loading' }),
    ...mapState('mealLock', { locks: 'locks', loadingLocks: 'loading' }),
    backText() {
      return mealTypeFullLabel(this.slot.type || 'fixed')
    },
    backHref() {
      return this.slot.type === 'supplementary' ? '../supplementary-meals' : '../fixed-meals'
    }
  },
  async created() {
    await this.loadSlot()
    const groups = await MealGroup.fetch({ _limit: -1, _sort: 'title:ASC' })
    this.groupOptions = groups
    await this.reloadConfigs()
  },
  methods: {
    ...mapActions('mealSlot', { fetchSlot: 'fetchOne', removeSlot: 'remove' }),
    ...mapActions('mealGroupConfig', {
      fetchConfigs: 'fetchBySlot',
      createConfig: 'create',
      updateConfig: 'update',
      removeConfig: 'remove'
    }),
    ...mapActions('mealLock', {
      fetchLocks: 'fetchList',
      createLock: 'create',
      updateLock: 'update',
      removeLock: 'remove'
    }),
    timeOfDayLabel,
    serveDaysLabel,
    feeTypeLabel,
    defaultRegisterLabel,
    defaultRegisterColor,
    emptyApp() {
      return {
        id: null,
        meal_group: null,
        isDefaultRegister: true,
        autoBalanceMenu: false,
        serveDays: null,
        registerOpenBefore: '14d',
        registerCloseBefore: '1d',
        cancelBefore: '1d',
        registerOpenDate: null,
        registerCloseDate: null,
        cancelDate: null
      }
    },
    formatMoney(v) {
      return (v || 0).toLocaleString('vi-VN') + ' đ'
    },
    async loadSlot() {
      this.slot = await this.fetchSlot(this.$route.params.id)
    },
    openEditMeal() {
      this.$refs.formDialog.openEdit(this.slot)
    },
    async onMealSaved() {
      await this.loadSlot()
      this.refreshHistory()
    },
    async reloadConfigs() {
      await this.fetchConfigs(this.$route.params.id)
      await this.reloadLocks()
    },
    async reloadLocks() {
      if (this.slot.type !== 'fixed') {
        this.$store.commit('mealLock/changeState', { locks: [] })
        return
      }
      await this.fetchLocks({ meal_slot: this.$route.params.id })
    },
    refreshHistory() {
      if (this.$refs.history) this.$refs.history.refresh()
    },
    openApplication(item) {
      if (item) {
        this.appForm = {
          id: item.id,
          meal_group: item.meal_group && item.meal_group.id,
          isDefaultRegister: item.isDefaultRegister,
          autoBalanceMenu: item.autoBalanceMenu,
          serveDays: item.serveDays || null,
          registerOpenBefore: item.registerOpenBefore || '14d',
          registerCloseBefore: item.registerCloseBefore || '1d',
          cancelBefore: item.cancelBefore || '1d',
          registerOpenDate: item.registerOpenDate ? moment(item.registerOpenDate).format('YYYY-MM-DD') : null,
          registerCloseDate: item.registerCloseDate ? moment(item.registerCloseDate).format('YYYY-MM-DD') : null,
          cancelDate: item.cancelDate ? moment(item.cancelDate).format('YYYY-MM-DD') : null
        }
      } else {
        this.appForm = this.emptyApp()
      }
      this.appDialog = true
      this.$nextTick(() => this.$refs.appForm && this.$refs.appForm.resetValidation())
    },
    async saveApplication() {
      if (!this.$refs.appForm.validate()) return

      // Bữa bổ sung: bắt buộc nhập đủ 3 ngày + kiểm tra logic thứ tự thời gian
      if (this.slot.type !== 'fixed') {
        const { registerOpenDate, registerCloseDate, cancelDate } = this.appForm
        if (!registerOpenDate || !registerCloseDate || !cancelDate) {
          this.$alert.error('Vui lòng nhập đủ Ngày mở ĐK / Ngày đóng ĐK / Ngày được hủy')
          return
        }
        const mOpen = moment(registerOpenDate)
        const mClose = moment(registerCloseDate)
        const mCancel = moment(cancelDate)
        const mServe = this.slot.serveDate ? moment(this.slot.serveDate) : null
        if (mOpen.isAfter(mClose, 'day')) {
          this.$alert.error('Ngày mở đăng ký không được sau ngày đóng đăng ký')
          return
        }
        if (mCancel.isBefore(mOpen, 'day')) {
          this.$alert.error('Ngày được hủy không được trước ngày mở đăng ký')
          return
        }
        if (mServe) {
          if (mClose.isAfter(mServe, 'day')) {
            this.$alert.error('Ngày đóng đăng ký không được sau ngày diễn ra bữa (' + mServe.format('DD/MM/YYYY') + ')')
            return
          }
          if (mCancel.isAfter(mServe, 'day')) {
            this.$alert.error('Ngày được hủy không được sau ngày diễn ra bữa (' + mServe.format('DD/MM/YYYY') + ')')
            return
          }
        }
      }

      this.savingApp = true
      try {
        const isReg = this.appForm.isDefaultRegister
        const payload = {
          meal_slot: this.$route.params.id,
          meal_group: this.appForm.meal_group,
          isDefaultRegister: isReg,
          autoBalanceMenu: isReg ? this.appForm.autoBalanceMenu : false
        }
        // Cấu hình thời gian đăng ký theo loại bữa (BE tự xoá field thừa theo loại bữa)
        if (this.slot.type === 'fixed') {
          payload.serveDays = this.appForm.serveDays || null
          payload.registerOpenBefore = this.appForm.registerOpenBefore
          payload.registerCloseBefore = this.appForm.registerCloseBefore
          payload.cancelBefore = this.appForm.cancelBefore
        } else {
          payload.registerOpenDate = this.appForm.registerOpenDate || null
          payload.registerCloseDate = this.appForm.registerCloseDate || null
          payload.cancelDate = this.appForm.cancelDate || null
        }
        if (this.appForm.id) {
          await this.updateConfig({ id: this.appForm.id, ...payload })
          this.$alert.updateSuccess()
        } else {
          await this.createConfig(payload)
          this.$alert.addSuccess()
        }
        this.appDialog = false
        await this.reloadConfigs()
        this.refreshHistory()
      } catch (err) {
        this.$alert.error('Lưu áp dụng thất bại: ' + (err.response ? err.response.data.message : err))
      } finally {
        this.savingApp = false
      }
    },
    confirmDeleteApp(item) {
      this.$dialog.confirm({
        title: 'Xác nhận',
        text: 'Xóa áp dụng này (và các khóa liên quan)?',
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeConfig(item.id)
            this.$alert.deleteSuccess()
            await this.reloadConfigs()
            this.refreshHistory()
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    },
    openLock() {
      this.lockForm = {
        id: null,
        meal_group: null,
        fromDate: moment()
          .add(1, 'day')
          .format('YYYY-MM-DD'),
        toDate: null
      }
      this.lockDialog = true
      this.$nextTick(() => this.$refs.lockForm && this.$refs.lockForm.resetValidation())
    },
    openLockEdit(item) {
      this.lockForm = {
        id: item.id,
        meal_group: item.meal_group && item.meal_group.id,
        fromDate: item.fromDate ? moment(item.fromDate).format('YYYY-MM-DD') : null,
        toDate: item.toDate ? moment(item.toDate).format('YYYY-MM-DD') : null
      }
      this.lockDialog = true
      this.$nextTick(() => this.$refs.lockForm && this.$refs.lockForm.resetValidation())
    },
    async saveLock() {
      if (!this.$refs.lockForm.validate()) return
      this.savingLock = true
      try {
        const payload = {
          meal_slot: this.$route.params.id,
          meal_group: this.lockForm.meal_group,
          fromDate: this.lockForm.fromDate,
          toDate: this.lockForm.toDate || null
        }
        if (this.lockForm.id) {
          await this.updateLock({ id: this.lockForm.id, ...payload })
          this.$alert.updateSuccess()
        } else {
          await this.createLock(payload)
          this.$alert.addSuccess()
        }
        this.lockDialog = false
        await this.reloadLocks()
        this.refreshHistory()
      } catch (err) {
        this.$alert.error('Lưu khóa thất bại: ' + (err.response ? err.response.data.message : err))
      } finally {
        this.savingLock = false
      }
    },
    confirmDeleteLock(item) {
      this.$dialog.confirm({
        title: 'Xác nhận',
        text: 'Xóa khóa này?',
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeLock(item.id)
            this.$alert.deleteSuccess()
            await this.reloadLocks()
            this.refreshHistory()
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    },
    confirmDeleteMeal() {
      this.$dialog.confirm({
        title: 'Xác nhận xóa bữa',
        text: `Xóa bữa "${this.slot.title}" cùng toàn bộ áp dụng và khóa của bữa?`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeSlot(this.$route.params.id)
            this.$alert.deleteSuccess()
            this.$router.push(this.backHref.replace('../', '/'))
          } catch (e) {
            this.$alert.deleteError(e)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
/* Đưa label sang trái, ô tick sang phải cho checkbox có class .label-left */
.label-left >>> .v-input__slot {
  flex-direction: row-reverse;
  justify-content: flex-end;
}
/* .label-left >>> .v-input--selection-controls__input {
  margin: 0 0 0 8px;
} */
</style>
